import { urls } from '~/preloaded';
import settings from '~/settings';
import { API_ERROR_REASONS, BATTLE_TYPES, REQUEST_STATUSES } from '~/constants';
import { get, patch, post } from '~/helpers/api';
import { getLastSeasonOfType } from '~/helpers/ladder';
import Loader from '~/helpers/loader';
import { t } from '~/helpers/localization';
import { getSeasonBattleType } from '~/helpers/seasons';
import { ROLE_NAMES } from '~/roles';
import { currentClanSelector } from '~/store/selectors/currentAccountSelector';
import { currentAccountActions } from '~/store/slices/currentAccountSlice';
import { ENTRY_SOURCE } from '~/store/slices/settingsSlice';

import { sendErrorNotification } from '~/web2ClientAPI/base';
import {
  sendAcceptApplicationClanIsFullErrorNotification,
  sendAcceptApplicationErrorWithPlayerInfo,
  sendAcceptApplicationNotification,
  sendAcceptApplicationPermissionErrorNotification,
  sendAcceptApplicationPlayerHasPermamentBanErrorNotification,
  sendAcceptApplicationPlayerInClanCooldown,
  sendAcceptApplicationPlayerInOtherClanErrorNotification,
  sendAcceptApplicationWithExpiredStatusErrorNotification,
  sendDeclineApplicationAlreadyAcceptedErrorNotification,
  sendDeclineApplicationErrorNotification,
  sendDeclineApplicationNotification,
  sendDeclineApplicationsNotification,
  sendSendApplicationErrorNotification,
  sendSendApplicationNotification,
} from '~/web2ClientAPI/notifications';

import { actionsApplications, type IClanApplication } from '~/Actions/ActionApplications';
import { actionsClanProfile } from '~/Actions/ActionClanProfile';
import { actionsRequests } from '~/Actions/ActionRequests';

import type { ClanInfoType } from '~/Actions/ActionClanProfile';
import type { ClanRecommendation } from '~/Actions/ActionRecommendations';
import type { AxiosCustomError } from '~/helpers/api';
import type { AppAsyncThunk, IAppDispatch } from '~/store';
import type {
  IApiGetApplicationsResponse,
  IApiGetApplicationsPayload,
  IApiAcceptApplicationResponse,
  IApiAcceptApplicationError,
  IApiAcceptApplicationAccountInCooldownError,
  IApiSendApplicationResponse,
  IApiSendApplicationPayload,
  IApiSendApplicationError,
  IApiDeclineApplicationResponse,
  IApiDeclineApplicationError,
  IApiDeclineApplicationsResponse,
} from '~/types/api';
import type { ClanInSearch, ClanPreloaded, ClanSearchResultItem } from '~/types/declaration';
import type { Nullable } from '~/types/generics';

const processAcceptError = (
  dispatch: IAppDispatch,
  error: Nullable<IApiAcceptApplicationError>,
  application: IClanApplication,
  currentClan: ClanInfoType | ClanPreloaded,
) => {
  const reason = error?.title;
  const playerName = application.account.name;

  switch (reason) {
    case API_ERROR_REASONS.ACCOUNT_BANNED: {
      sendAcceptApplicationPlayerHasPermamentBanErrorNotification(playerName);
      break;
    }

    case API_ERROR_REASONS.ACCOUNT_IN_COOLDOWN: {
      const expire = (error as IApiAcceptApplicationAccountInCooldownError)?.additional_data.ban_expiration_time;
      sendAcceptApplicationPlayerInClanCooldown(playerName, expire);
      break;
    }

    case API_ERROR_REASONS.APPLICATION_IS_NOT_ACTIVE: {
      sendAcceptApplicationWithExpiredStatusErrorNotification(playerName);
      dispatch(currentAccountActions.decreaseActiveEntriesCount());
      dispatch(actionsApplications.processingSuccess([application.id]));
      break;
    }

    case API_ERROR_REASONS.ACCOUNT_ALREADY_IN_CLAN: {
      sendAcceptApplicationPlayerInOtherClanErrorNotification(playerName);
      break;
    }

    case API_ERROR_REASONS.INSUFFICIENT_PERMISSIONS: {
      sendAcceptApplicationPermissionErrorNotification(playerName);
      break;
    }

    case API_ERROR_REASONS.CLAN_IS_FULL: {
      if (currentClan) {
        sendAcceptApplicationClanIsFullErrorNotification(playerName, currentClan);
      } else {
        sendAcceptApplicationErrorWithPlayerInfo(playerName);
      }
      break;
    }

    default:
      sendAcceptApplicationErrorWithPlayerInfo(playerName);
  }

  dispatch(actionsApplications.processingFailed([application.id]));
};

export const acceptApplicationThunk =
  (application: IClanApplication): AppAsyncThunk =>
  async (dispatch, getState) => {
    dispatch(actionsApplications.startProcessing([application.id]));

    try {
      const url = `${urls.applications}${application.id}/`;
      const payload = { status: REQUEST_STATUSES.ACCEPTED };
      const response = await patch<IApiAcceptApplicationResponse | IApiAcceptApplicationError>(url, payload);

      if ('title' in response) {
        throw response;
      }

      const state = getState();
      const currentClan = currentClanSelector(state);
      if (currentClan) {
        const player = {
          name: application.account.name,
          roleName: ROLE_NAMES.PRIVATE,
        };
        sendAcceptApplicationNotification(player, currentClan);
      }

      if (
        state.applications.page === Math.ceil(state.applications?.meta?.total || 0 / settings.entries.limit) &&
        state.applications.applications.length === 1 &&
        state.applications.page - 1 !== 0
      ) {
        dispatch(actionsApplications.changePage(state.applications.page - 1));
      }

      void dispatch(fetchApplicationsThunk());

      dispatch(currentAccountActions.decreaseActiveEntriesCount());
      if (currentClan) {
        dispatch(actionsClanProfile.increaseClanMembersCount(currentClan.id, 1));
      }
      dispatch(actionsApplications.processingSuccess([application.id]));
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiAcceptApplicationError>;

      const clan = currentClanSelector(getState());
      if (!clan) {
        sendErrorNotification();
        return;
      }

      if (axiosError.data) {
        processAcceptError(dispatch, axiosError.data, application, clan);
      } else {
        processAcceptError(dispatch, null, application, clan);
      }
    }
  };

const processDeclineError = (
  dispatch: IAppDispatch,
  data: Nullable<IApiDeclineApplicationError>,
  application: IClanApplication,
  currentClan: {
    color: string;
    id: number;
    name: string;
    tag: string;
  },
) => {
  const reason = data?.title;
  const playerName = application.account.name;

  if (reason === API_ERROR_REASONS.APPLICATION_IS_NOT_ACTIVE) {
    const applicationStatus = data?.additional_data?.status;
    if (applicationStatus === REQUEST_STATUSES.ACCEPTED) {
      sendDeclineApplicationAlreadyAcceptedErrorNotification(playerName, currentClan);
    }

    dispatch(currentAccountActions.decreaseActiveEntriesCount());
    dispatch(actionsApplications.processingSuccess([application.id]));

    return;
  }

  if (reason === API_ERROR_REASONS.ACCOUNT_ALREADY_IN_CLAN) {
    const clanId = data?.additional_data?.clan_id;
    if (clanId === currentClan.id) {
      sendDeclineApplicationAlreadyAcceptedErrorNotification(playerName, currentClan);
    }
  } else {
    sendDeclineApplicationErrorNotification();
  }

  dispatch(actionsApplications.processingFailed([application.id]));
};

export const declineApplicationsThunk = (): AppAsyncThunk => async (dispatch, getState) => {
  const state = getState();
  const applicationsIds = state.applications.selectedApplicationsPlayersIds;
  dispatch(actionsApplications.startProcessing(applicationsIds));

  try {
    const url = urls.applicationsManage;
    const payload = { ids: applicationsIds };
    const response = await post<IApiDeclineApplicationsResponse>(url, payload);

    if (Object.keys(response.fail_details).length) {
      sendDeclineApplicationErrorNotification();
      dispatch(actionsApplications.processingFailed(applicationsIds));
      return;
    }

    sendDeclineApplicationsNotification();

    if (
      state.applications.page === Math.ceil(state.applications?.meta?.total || 0 / settings.entries.limit) &&
      state.applications.applications.length === applicationsIds.length &&
      state.applications.page - 1 !== 0
    ) {
      dispatch(actionsApplications.changePage(state.applications.page - 1));
    }

    void dispatch(fetchApplicationsThunk());

    dispatch(actionsApplications.toggleAllApplicationsPlayersTick());
    dispatch(actionsApplications.processingSuccess(applicationsIds));
  } catch (error: unknown) {
    sendDeclineApplicationErrorNotification();
    dispatch(actionsApplications.processingFailed(applicationsIds));
  }
};

export const declineApplicationThunk =
  (application: IClanApplication): AppAsyncThunk =>
  async (dispatch, getState) => {
    dispatch(actionsApplications.startProcessing([application.id]));

    try {
      const url = `${urls.applications}${application.id}/`;
      const payload = { status: REQUEST_STATUSES.DECLINED };
      const response = await patch<IApiDeclineApplicationResponse | IApiDeclineApplicationError>(url, payload);

      if ('title' in response) {
        throw response;
      }

      const state = getState();
      const currentClan = currentClanSelector(state);
      if (currentClan) {
        sendDeclineApplicationNotification(application.account.name, currentClan);
      }

      if (
        state.applications.page === Math.ceil(state.applications?.meta?.total || 0 / settings.entries.limit) &&
        state.applications.applications.length === 1 &&
        state.applications.page - 1 !== 0
      ) {
        dispatch(actionsApplications.changePage(state.applications.page - 1));
      }

      void dispatch(fetchApplicationsThunk());

      dispatch(currentAccountActions.decreaseActiveEntriesCount());
      dispatch(actionsApplications.processingSuccess([application.id]));
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiDeclineApplicationError>;

      const clan = currentClanSelector(getState());
      if (!clan) {
        sendErrorNotification();
        return;
      }

      if (axiosError.data) {
        processDeclineError(dispatch, axiosError.data, application, clan);
      } else {
        processDeclineError(dispatch, null, application, clan);
      }
    }
  };

export const fetchApplicationsThunk =
  (withGlobalSpinner?: boolean): AppAsyncThunk =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.applications.isFetching) {
      return;
    }

    const battleType = state.requests.requestsBattleType;
    const clanSeasonType = getSeasonBattleType(battleType);

    let currentSeason: number = state.requests.requestsSeason;

    dispatch(actionsApplications.toggleFetching());

    if (clanSeasonType && state.requests.requestsSeasonType !== clanSeasonType) {
      dispatch(actionsRequests.changeRequestsSeasonType(clanSeasonType));
      const seasonTypeId = getLastSeasonOfType(clanSeasonType)?.id;
      if (seasonTypeId) {
        currentSeason = seasonTypeId;
        dispatch(actionsRequests.changeRequestsSeason(currentSeason));
      }
    }

    const currentAccount = state.currentAccount;

    const loader = withGlobalSpinner ? new Loader(t('Загружаем заявки')) : null;
    loader?.start(dispatch);

    try {
      const url = currentAccount.clanId ? urls.applicationsClanList : urls.applicationsAccountList;
      const limit = settings.entries.limit;
      const order = state.applications.sort.order;
      const payload: IApiGetApplicationsPayload = {
        battle_type: clanSeasonType ? BATTLE_TYPES.REGULAR_CVC : state.requests.requestsBattleType,
        order: state.applications.sort.isAsc ? order : `-${order}`,
        offset: (state.applications.page - 1) * limit,
        season_id: clanSeasonType ? currentSeason : undefined,
        limit,
      };
      const response = await get<IApiGetApplicationsResponse>(url, payload);

      if (response.error) {
        dispatch(actionsApplications.updateError(response.error));
        return;
      }

      const activeApplicationsCount = response?._meta_?.total ?? currentAccount.activeApplicationsCount;
      dispatch(currentAccountActions.updateAccountInfo({ activeApplicationsCount }));
      dispatch(actionsApplications.updateApplications(response));
    } catch (error: unknown) {
      dispatch(
        actionsApplications.updateError({
          error: t('Произошла ошибка. Повторите попытку позже'),
        }),
      );
    } finally {
      loader?.stop(dispatch);
    }
  };

const processSendError = (
  data: Nullable<IApiSendApplicationError>,
  clan: {
    color: string;
    id: number;
    name: string;
    tag: string;
  },
) => {
  const reason = data?.additional_data?.reason;
  sendSendApplicationErrorNotification(reason || 'default', clan);
};

const ENTRY_SOURCES = settings.entrySources;

export const sendApplicationThunk =
  (
    clan: ClanInfoType | ClanRecommendation | ClanInSearch | ClanSearchResultItem,
    sourceName: ENTRY_SOURCE,
    shouldFetchApplications = false,
    info = '',
  ): AppAsyncThunk =>
  async (dispatch) => {
    dispatch(actionsApplications.toggleJoinClanFetching());
    const source = ENTRY_SOURCES[sourceName] || ENTRY_SOURCES[ENTRY_SOURCE.CLAN_PROFILE];

    try {
      const url = urls.applications;
      const payload: IApiSendApplicationPayload = {
        recipient_id: clan.id,
        source: source,
        comment: info,
      };
      const response = await post<IApiSendApplicationResponse>(url, payload);

      sendSendApplicationNotification({
        name: clan.name,
        tag: clan.tag,
        color: 'color' in clan ? clan.color : clan.hex_color,
      });

      dispatch(actionsClanProfile.updateClanActiveApplications(clan.id, response.application.id));

      if (shouldFetchApplications) {
        void dispatch(fetchApplicationsThunk(true));
      }
    } catch (error: unknown) {
      const axiosError = error as AxiosCustomError<IApiSendApplicationError>;
      if (axiosError?.data) {
        processSendError(axiosError.data, {
          id: clan.id,
          name: clan.name,
          tag: clan.tag,
          color: 'color' in clan ? clan.color : clan.hex_color,
        });
      } else {
        processSendError(null, {
          id: clan.id,
          name: clan.name,
          tag: clan.tag,
          color: 'color' in clan ? clan.color : clan.hex_color,
        });
      }
    } finally {
      dispatch(actionsApplications.toggleJoinClanFetching());
    }
  };
